import { ReactElement, useMemo, useState } from 'react';
import { MultiSelect, SearchField, VenueIcon } from '@weareopenr/component-library';
import SelectableListWithControls from 'src/components/SelectableListWithControls/SelectableListWithControls';
import { SelectableListOption } from 'src/components/SelectableList/SelectableList';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import { IMenu } from 'src/types/menu.types';
import styles from './MenuSetMenusStep.module.scss';

interface IMenuSetMenusStepProps {
  menus: IMenu[];
  selectedMenuIds: string[];
  onMenusChange: (menuIds: string[]) => void;
  isLoading: boolean;
}

const toOption = (menu: IMenu): SelectableListOption => ({
  id: menu.menuId,
  label: menu.name,
  subtitle: menu.ranges.map(({ name }) => name).join(', '),
});

export const MenuSetMenusStep = ({
  menus,
  selectedMenuIds,
  onMenusChange,
  isLoading,
}: IMenuSetMenusStepProps): ReactElement => {
  const [searchTerm, setSearchTerm] = useState('');
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const debouncedSearchTerm = useDebounce(searchTerm);

  const labelOptions = useMemo(() => {
    const labels = new Map<string, string>(
      menus.flatMap(({ ranges }) => ranges.map(({ range_id, name }): [string, string] => [range_id, name])),
    );
    return Array.from(labels, ([id, label], index) => ({ id, label, display_order: index }));
  }, [menus]);

  const allOptions = useMemo(() => menus.map(toOption), [menus]);
  const options = useMemo(() => {
    const labelledMenuIds = new Set(
      menus
        .filter(({ ranges }) => ranges.some(({ range_id }) => labelIds.includes(range_id)))
        .map(({ menuId }) => menuId),
    );
    return allOptions.filter(
      ({ id, label }) =>
        (!debouncedSearchTerm || label.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) &&
        (labelIds.length === 0 || labelledMenuIds.has(id)),
    );
  }, [menus, allOptions, debouncedSearchTerm, labelIds]);
  const selectedOptions = useMemo(
    () => allOptions.filter(({ id }) => selectedMenuIds.includes(id)),
    [allOptions, selectedMenuIds],
  );
  const isFiltered = Boolean(debouncedSearchTerm) || labelIds.length > 0;

  return (
    <SelectableListWithControls
      options={options}
      selectedOptionsIds={selectedMenuIds}
      onOptionChange={onMenusChange}
      scrollContainerClassName={styles['list']}
      collapsible={{ selectedOptionsData: selectedOptions, isPinned: true }}
      listDetails={{
        title: 'Menus:',
        emptyMessage: isFiltered ? 'No menus match your search.' : 'There are no menus available for this menu source.',
        isLoading,
      }}
    >
      <SearchField isDisabled={false} placeholder="Search for a menu" onChange={(e) => setSearchTerm(e.target.value)} />
      {labelOptions.length > 0 && (
        <MultiSelect
          Icon={VenueIcon}
          name="menu-labels"
          label="Menu labels"
          className={styles['labels-filter']}
          options={labelOptions}
          selected={labelIds}
          onChange={setLabelIds}
        />
      )}
    </SelectableListWithControls>
  );
};

export default MenuSetMenusStep;
